import { ElementRef, Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class SvgPanZoomService {
  private svgElement!: SVGSVGElement;
  private containerElement!: HTMLElement;

  private zoomFactor = 1.2;
  private minZoom = 1;
  private maxZoom = 4;
  private currentZoom = 1;

  private panX = 0;
  private panY = 0;
  private startX = 0;
  private startY = 0;
  private isPanning = false;

  initialViewBox = '';

  initialize(svgElement: SVGSVGElement, containerElement: HTMLElement) {
    this.svgElement = svgElement;
    this.containerElement = containerElement;
    this.initialViewBox = this.svgElement.getAttribute('viewBox') || '';
  }    

  get viewBox(): string {
    return this.svgElement.getAttribute('viewBox') || '';
  }

  set viewBox(value: string) {
    this.svgElement.setAttribute('viewBox', value);
  }

  zoomIn() {
    const rect = this.svgElement.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    this.applyZoom(this.zoomFactor, centerX, centerY);
  }

  zoomOut(initialViewBox: string) {
    const rect = this.svgElement.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    this.applyZoom(1 / this.zoomFactor, centerX, centerY);

    if (this.currentZoom <= this.minZoom) {
      this.resetZoom(initialViewBox);
    }
  }

  resetZoom(initialViewBox: string) {
    this.currentZoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.viewBox = initialViewBox;
  }

  onWheel(event: WheelEvent, initialViewBox: string) {
    if (!event.ctrlKey) return;
    event.preventDefault();

    const rect = this.svgElement.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    const scaleFactor = event.deltaY > 0 ? 1 / this.zoomFactor : this.zoomFactor;

    if(event.deltaY > 0 && this.currentZoom === 1) {
      this.resetZoom(initialViewBox);
      return;
    }

    this.applyZoom(scaleFactor, mouseX, mouseY);
  }

  applyZoom(scaleFactor: number, centerX?: number, centerY?: number) {
    if (!this.svgElement) return;
  
    let [x, y, width, height] = this.viewBox.split(' ').map(Number);
    const newWidth = width / scaleFactor;
    const newHeight = height / scaleFactor;
  
    if (this.currentZoom * scaleFactor < this.minZoom || this.currentZoom * scaleFactor > this.maxZoom) {
      return;
    }
  
    this.currentZoom *= scaleFactor;
    
    if (this.currentZoom < 1.19) {
      this.resetZoom(this.initialViewBox);
      return;
    }
    
    if (centerX !== undefined && centerY !== undefined) {
      const svgX = x + (centerX / this.containerElement.clientWidth) * width;
      const svgY = y + (centerY / this.containerElement.clientHeight) * height;
  
      x = svgX - (centerX / this.containerElement.clientWidth) * newWidth;
      y = svgY - (centerY / this.containerElement.clientHeight) * newHeight;
    }
  
    this.viewBox = `${x} ${y} ${newWidth} ${newHeight}`;
  }

  startPan(event: MouseEvent | Touch) {
    if (this.currentZoom === 1) return;
  
    this.isPanning = true;
    this.startX = event.clientX;
    this.startY = event.clientY;
  }
  
  pan(event: MouseEvent | Touch) {
    if (!this.isPanning || this.currentZoom === 1) return;
  
    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;
    this.startX = event.clientX;
    this.startY = event.clientY;
  
    let [x, y, width, height] = this.viewBox.split(' ').map(Number);
    
    const newX = x - deltaX * (width / this.containerElement.clientWidth);
    const newY = y - deltaY * (height / this.containerElement.clientHeight);
    
    const [initialX, initialY, initialWidth, initialHeight] = this.initialViewBox.split(' ').map(Number);
    
    const minX = initialX;
    const minY = initialY;
    const maxX = initialX + (initialWidth - width);
    const maxY = initialY + (initialHeight - height);
    
    x = Math.max(minX, Math.min(maxX, newX));
    y = Math.max(minY, Math.min(maxY, newY));
  
    this.viewBox = `${x} ${y} ${width} ${height}`;
  }

  stopPan() {
    this.isPanning = false;
  }

  get isZoomed() {
    return this.currentZoom !== 1 || this.panX !== 0 || this.panY !== 0;
  }
}
